import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { WarehouseOrder, WarehouseOrderItem, Order, ShipmentNote } from '../types';
import useWarehouseOrdersTable from '../hooks/useWarehouseOrdersTable';
import OrderScannerDialog from './OrderScannerDialog';
import api from '../utils/api';
import '../core.css';
import '../OrderDetailsDialog.css';
import LoadingSpinner from './LoadingSpinner';

interface WarehouseOrdersProps {
  isAdmin: boolean;
  warehouseId: number;
}

const WarehouseOrders: React.FC<WarehouseOrdersProps> = ({ isAdmin, warehouseId }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [orders, setOrders] = useState<WarehouseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [page, setPage] = useState(1);
  const [activeOrder, setActiveOrder] = useState<WarehouseOrder | null>(null);
  const [hideShipped, setHideShipped] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Memoized filtered orders to prevent unnecessary re-computations
  const filteredOrders = useMemo(
    () =>
      hideShipped
        ? orders.filter((order) =>
            order.shipments.some((s) => s.items.some((si) => si.status !== 'Shipped'))
          )
        : orders,
    [orders, hideShipped]
  );

  const { displayedData, totalPages, sortConfig, searchTerm, setSearchTerm, handleSort } =
    useWarehouseOrdersTable(filteredOrders, page);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchWarehouseOrders();
  }, [user, navigate]);

  // Deep link from the dashboard: ?order=123 filters to that order and opens it.
  useEffect(() => {
    const orderId = Number(new URLSearchParams(location.search).get('order'));
    if (!orderId || loading || !orders.length) return;
    setSearchTerm(String(orderId));
    const match = orders.find((o) => o.id === orderId);
    if (match) setActiveOrder(match);
    navigate(location.pathname, { replace: true });
  }, [location.search, location.pathname, loading, orders, navigate, setSearchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        setActiveOrder(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchWarehouseOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/orders?warehouse_id=${warehouseId}`);
      const data = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
      const mappedOrders: WarehouseOrder[] = data.flatMap((order: Order) =>
        order.shipments
          .filter((s: any) => s.warehouse_id === warehouseId && s.id !== null)
          .map((shipment: any) => {
            const items: WarehouseOrderItem[] = shipment.items?.map((item: any) => ({
              id: item.id,
              product_name: item.product_name,
              sku: item.sku || null,
              gtin: item.gtin || null,
              quantity: 1,
              status: item.status || 'Pending',
              shipping_label: shipment.shipping_label || null,
            })) || [];
            return {
              id: Number(order.id) || 0,
              order_key: order.order_key || '',
              date_created: order.date_created || new Date().toISOString(),
              customer_name: order.customer_name || 'N/A',
              customer_email: order.customer_email || null,
              customer_phone: order.customer_phone || null,
              customer_address: order.customer_address || null,
              customer_address_2: order.customer_address_2 || null,
              customer_city: order.customer_city || null,
              customer_state: order.customer_state || null,
              customer_country: order.customer_country || null,
              customer_zipcode: order.customer_zipcode || null,
              status: order.status || 'Pending',
              total: order.total || 0,
              shipping: order.shipping || 0,
              sales_tax: order.sales_tax || 0,
              date_completed: order.date_completed || null,
              last_updated: order.last_updated || new Date().toISOString(),
              customer_type: order.customer_type || null,
              customer_type_color: order.customer_type_color || '#e0e0e0',
              shipping_company: order.shipping_company || null,
              shipping_address: order.shipping_address || null,
              shipping_address_2: order.shipping_address_2 || null,
              shipping_city: order.shipping_city || null,
              shipping_state: order.shipping_state || null,
              shipping_country: order.shipping_country || null,
              shipping_zipcode: order.shipping_zipcode || null,
              customer_provided_note: order.customer_provided_note || null,
              shipments: [shipment],
              items,
              shipment_reference: shipment.shipment_reference || null,
              tracking_number: shipment.tracking_number || null,
              carrier: shipment.carrier || null,
            };
          })
      ).filter((order) => order.shipments.length > 0);
      setOrders(mappedOrders);
      setLastUpdated(
        response.data.last_updated ||
          new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZone: 'America/Toronto',
          })
      );
    } catch (error: any) {
      console.error('Fetch warehouse orders error:', error.response?.data);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/login');
      } else {
        setError(error.response?.data?.error || 'Failed to fetch orders');
        showToast(error.response?.data?.error || 'Failed to fetch orders', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Update the title to reflect the warehouse
  const warehouseName = warehouseId === 1 ? 'Gelinas' : 'MPP';

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const openOrderDialog = (order: WarehouseOrder) => {
    console.log('Opening dialog for order:', order);
    setActiveOrder(order);
  };

  // When the label was created and the shipment sent to this warehouse
  // (saved on the shipment when the label is made, and never changed after)
  const labelDate = (order: WarehouseOrder): string | null =>
    order.shipments[0]?.date_shipped || null;

  const getRowColor = (item: WarehouseOrderItem, order: WarehouseOrder) => {
    const shipment = order.shipments.find((s) =>
      s.items.some((si) => si.id === item.id)
    );
    const itemStatus = shipment?.items.find((si) => si.id === item.id)?.status || item.status || 'Pending';
    if (itemStatus === 'Shipped') return 'shipped-item';
    // How long the warehouse has had it: from when the label was sent
    const sentDate = new Date(labelDate(order) || order.date_created);
    const today = new Date();
    const diffDays = Math.floor(
      (today.getTime() - sentDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays > 1) return 'red-row';
    if (diffDays === 1) return 'yellow-row';
    return 'grey-row';
  };

  const columns = [
    { key: 'id', label: 'Order #', sortKey: 'id' as const },
    { key: 'date_created', label: 'Order Date', sortKey: null },
    { key: 'label_date', label: 'Label Date', sortKey: null },
    { key: 'customer_name', label: 'Customer Name', sortKey: 'customer_name' as const },
    { key: 'items', label: 'Items', sortKey: null },
    { key: 'status', label: 'Status', sortKey: 'status' as const },
  ];

  if (error) return <div className="container">Error: {error}</div>;
  if (loading) return <LoadingSpinner />;

  return (
    <div className="stock-table-container">
      <h1>{warehouseName} Warehouse Orders</h1>
      <p className="last-updated">Last Updated: {lastUpdated}</p>
      <div className="filter-container">
        <label>
          <input
            type="checkbox"
            checked={hideShipped}
            onChange={(e) => setHideShipped(e.target.checked)}
          />
          Hide Shipped Orders
        </label>
      </div>
      <div className="search-container">
        <input
          type="text"
          placeholder="Search orders (e.g., ID, customer, status, product)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>
      <div className="table-header">
        <p>Total Orders: {filteredOrders.length}</p>
        <div className="pagination">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      </div>
      <table className="orders-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={col.sortKey ? () => handleSort(col.sortKey) : undefined}
                style={{ cursor: col.sortKey ? 'pointer' : 'default' }}
              >
                {col.label}
                {sortConfig.key === col.sortKey && (sortConfig.order === 'asc' ? ' ↑' : ' ↓')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayedData.map((order, index) => (
            <tr
              key={`${order.id}-${order.shipment_reference}-${index}`}
              onClick={() => openOrderDialog(order)}
              style={{ cursor: 'pointer' }}
            >
              <td data-label="Order #">{order.id || 'N/A'}</td>
              <td data-label="Order Date">
                {new Date(order.date_created).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </td>
              <td data-label="Label Date">
                {labelDate(order)
                  ? new Date(labelDate(order)!).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      timeZone: 'America/Toronto',
                    })
                  : '—'}
              </td>
              <td data-label="Customer Name">{order.customer_name}</td>
              <td data-label="Items" style={{ textAlign: 'left' }}>
                {order.items.map((item) => (
                  <div key={item.id} className={getRowColor(item, order)}>
                    {`${item.product_name} (${item.status})`}
                    {item.shipping_label && (
                      <div>
                        <a
                          href={item.shipping_label}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="carrier-chip"
                        >
                          Shipping Label
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </td>
              <td data-label="Status">
                <span
                  className="supplier-tag"
                  data-status={order.shipments[0]?.items.every((si) => si.status === 'Shipped') ? 'Shipped' : 'Pending'}
                >
                  {order.shipments[0]?.items.every((si) => si.status === 'Shipped') ? 'Shipped' : 'Pending'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {activeOrder && (
        <OrderScannerDialog
          order={activeOrder}
          onClose={() => setActiveOrder(null)}
          dialogRef={dialogRef}
          refreshOrders={fetchWarehouseOrders}
          warehouseId={warehouseId}
        />
      )}
    </div>
  );
};

export default WarehouseOrders;